import React from 'react';
import analytics from '@ublend-npm/aula-analytics';
import { Transforms, Text, Element, Editor, Range } from 'slate';
import { ReactEditor } from 'slate-react';
import { getAnalyticsEvent } from '../../lib/useAnalytics';
import {
  getCurrentNode,
  isEmptyElement,
  isCurrentSelectionCollapsed,
} from '../../lib/queries';
import { AulaPlugin } from '../plugins';
import {
  orderedListType,
  unorderedListType,
  listItemType,
  isListItem,
  isList,
} from './listType';
import { Ol, Ul, Li } from './list.styled';
import { listNormalizer } from './listNormalizer';
import { EditorState } from '../../Editor.types';
import { startLocation } from '../../utils';

let isComposing = false; // IME Composing going on
let hasCompositionJustEnded = false; // Used to swallow keyup event related to compositionend

const listTypes: Array<string | undefined> = [
  orderedListType,
  unorderedListType,
  listItemType,
];

export const list: (
  onSend?: (editorState: EditorState) => void
) => AulaPlugin = (onSend) => (editableProps, editor, context) => {
  const { isVoid, normalizeNode } = editor;

  editor.isVoid = (el) => (listTypes.includes(el?.type) ? false : isVoid(el));

  editor.normalizeNode = (entry) =>
    listNormalizer({
      entry,
      editor,
      normalizeNode,
    });

  return {
    // When adding a plugin, spread the editableProps that are passed in
    ...editableProps,
    onKeyDown: (e) => {
      if (editableProps.onKeyDown) editableProps.onKeyDown(e);

      if (e.code === 'Space' && editor.selection != null) {
        const node = getCurrentNode(editor) as Element;

        if (
          Element.isElement(node) &&
          node.children.length &&
          Text.isText(node.children[0])
        ) {
          const { offset } = editor.selection.anchor;
          const { text } = node.children[0];

          // Checking for a list trigger
          const listTrigger: {
            type: 'ordered' | 'unordered' | null;
            position: 'start' | 'middle' | null;
          } = { type: null, position: null };
          if (offset === 2 && ['1.', '1)'].includes(text.slice(0, 2))) {
            listTrigger.type = 'ordered';
            listTrigger.position = 'start';
          } else if (
            offset > 2 &&
            ['\n1.', '\n1)'].includes(text.slice(offset - 3, offset))
          ) {
            listTrigger.type = 'ordered';
            listTrigger.position = 'middle';
          } else if (offset === 1 && text.slice(0, 1) === '-') {
            listTrigger.type = 'unordered';
            listTrigger.position = 'start';
          } else if (offset > 1 && text.slice(offset - 2, offset) === '\n-') {
            listTrigger.type = 'unordered';
            listTrigger.position = 'middle';
          }

          if (listTrigger.type) {
            e.preventDefault();
            Transforms.removeNodes(editor);
            if (listTrigger.position === 'middle') {
              Transforms.insertNodes(editor, {
                children: [
                  {
                    text: text.slice(
                      0,
                      offset - (listTrigger.type === 'ordered' ? 3 : 2)
                    ),
                  },
                ],
              });
            }
            Transforms.insertNodes(
              editor,
              {
                type:
                  listTrigger.type === 'ordered'
                    ? orderedListType
                    : unorderedListType,
                children: [
                  {
                    type: listItemType,
                    children: [{ children: [{ text: text.slice(offset) }] }],
                  },
                ],
              },
              { mode: 'highest' }
            );
          }
        }
      }

      if (e.key === 'Enter') {
        // Prevent to trigger the onChange flow when pressing the enter key
        e.preventDefault();
        if (!e.shiftKey) {
          // IME composing fires keydown/keyup events
          if (isComposing || hasCompositionJustEnded) {
            hasCompositionJustEnded = false;
            return;
          }
          if (typeof onSend === 'function') {
            return;
          }
        }

        const node = getCurrentNode(editor);
        if (Element.isElement(node) && Element.isElementList(node.children)) {
          if (isList(node)) {
            const [[currentItem, currentItemPath]] = Editor.nodes(editor, {
              mode: 'lowest',
              match: (n) =>
                !Editor.isEditor(n) && Element.isElement(n) && isListItem(n),
            });

            if (
              Element.isElement(currentItem) &&
              currentItem.children.length === 1 &&
              isEmptyElement(currentItem.children[0])
            ) {
              Transforms.removeNodes(editor, { at: currentItemPath });
              Transforms.insertNodes(
                editor,
                { children: [{ text: '' }] },
                { mode: 'highest' }
              );
              return;
            }

            Transforms.splitNodes(editor, {
              always: true,
              at: editor.selection as Range,
              match: (n) =>
                !Editor.isEditor(n) && Element.isElement(n) && isListItem(n),
            });
          }
        }
      }

      if (e.key === 'Backspace') {
        const { selection } = editor;

        if (selection == null) {
          return;
        }

        const isSelectionAtStart =
          (selection.anchor.path[0] === 0 && selection.anchor.path[1] === 0) ||
          (selection.focus.path[0] === 0 && selection.focus.path[1] === 0);

        if (!isSelectionAtStart) {
          return;
        }

        const isListAtBeginning =
          Array.from(
            Editor.nodes(editor, {
              at: startLocation,
              match: (n) =>
                !Editor.isEditor(n) && Element.isElement(n) && isList(n),
            })
          ).length !== 0;

        if (!isListAtBeginning) {
          return;
        }

        const isAnchorStart =
          selection.anchor.path[0] === 0 &&
          selection.anchor.path[1] === 0 &&
          selection.anchor.offset === 0;

        const isFocusStart =
          selection.focus.path[0] === 0 &&
          selection.focus.path[1] === 0 &&
          selection.focus.offset === 0;

        if (isAnchorStart && isFocusStart) {
          // one lift to replace item-list, and one more lift to replace list
          Transforms.liftNodes(editor);
          Transforms.liftNodes(editor);
        } else if (isAnchorStart || isFocusStart) {
          const allNodes = Array.from(
            Editor.nodes(editor, {
              at: selection,
              match: (n) => !Editor.isEditor(n) && Element.isElement(n),
              mode: 'highest',
            })
          );

          const [[firstNode, firstNodePath]] = allNodes;

          if (allNodes.length > 1) {
            Transforms.removeNodes(editor, {
              at: firstNodePath,
            });
          } else if (allNodes.length === 1) {
            const allItemNodes = Array.from(
              Editor.nodes(editor, {
                at: selection,
                match: (n) =>
                  !Editor.isEditor(n) && Element.isElement(n) && isListItem(n),
                mode: 'highest',
              })
            );

            const isAtListEnd =
              Editor.isEnd(editor, selection.anchor, selection.anchor.path) ||
              Editor.isEnd(editor, selection.focus, selection.focus.path);
            if (
              Element.isElement(firstNode) &&
              isAtListEnd &&
              allItemNodes.length === firstNode.children.length
            ) {
              Transforms.removeNodes(editor, {
                at: firstNodePath,
              });
              Transforms.insertNodes(
                editor,
                { children: [{ text: '' }] },
                { at: [0], mode: 'highest', select: true }
              );
            }
          }
        }
      }

      if (e.key === 'Tab') {
        const { selection } = editor;
        const node = getCurrentNode(editor);

        if (!selection || !node || !context) {
          return;
        }

        if (!isList(node)) {
          return;
        }

        if (!isCurrentSelectionCollapsed(editor)) {
          return;
        }

        const listItemContainerPath = selection.anchor.path[2];
        const listItemContentPath = selection.anchor.path[3];
        const isAtBeginningOfListItem =
          selection.anchor.offset === 0 &&
          listItemContainerPath === 0 &&
          listItemContentPath === 0;

        if (isAtBeginningOfListItem) {
          analytics.track(getAnalyticsEvent('ATTEMPT_TO_NEST_LIST', context));
        }
      }
    },
    onCompositionStart: () => {
      isComposing = true;
    },
    onCompositionEnd: () => {
      isComposing = false;
      // some browsers (IE, Firefox, Safari) send a keyup event after
      // compositionend, some (Chrome, Edge) don't. This is to swallow
      // the next keyup event, unless a keydown event happens first
      hasCompositionJustEnded = true;
    },
    renderElement: (props) => {
      const { attributes, children, element } = props;

      const { type, children: elementChildren } = element;

      switch (type) {
        case orderedListType: {
          return <Ol {...attributes}>{children}</Ol>;
        }
        case unorderedListType: {
          return <Ul {...attributes}>{children}</Ul>;
        }

        case listItemType: {
          const headingType =
            elementChildren &&
            elementChildren.length &&
            Element.isElement(elementChildren[0]) &&
            elementChildren[0].type;

          const [parent] = Editor.parent(
            editor,
            ReactEditor.findPath(editor, element)
          );

          const parentType = isList(parent) && parent.type;

          return (
            <Li {...attributes} type={headingType} parentType={parentType}>
              {children}
            </Li>
          );
        }
        default:
          return editableProps.renderElement?.(props) || <p />;
      }
    },
  };
};
